/**
 * seedrandom helper: uses the seedrandom library as a replacement for Math.random
 *
 * Usage:
 * ```
 * import { random } from "@/helpers/random";
 * const r = random();
 *````
 * The seed is set automatically in "development" mode to the value of "randomSeed" in @/data/config.json.
 *
 * In "production" mode, the seed is *not set* automatically and random results are restored to true (pseudo)randomness.
 * (Run `yarn serve --mode production` to test production mode.)
 *
 * Override the default seed by either:
 *  - setting the "randomSeed" value in @/data/config.json
 *  - importing (& calling) seedRNG(seed) from this module
 *
 * Call `seedRNG()` at any point without args to restore (psuedo)randomness.
 *
 * More useful utility functions exported from this module:
 * `shuffleArray(array)`
 * `randomRange(min, max)`
 * `randomRangeInt(min, max)`
 */

import seedrandom from "seedrandom";
import config from "@/data/config.json";

let rng = import.meta.env.DEV
  ? seedrandom(config.settings.randomSeed)
  : seedrandom();

function seedRNG(seed) {
  rng = seed ? seedrandom(seed) : seedrandom();
}

function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; --i) {
    const r = Math.floor(rng() * (i + 1));
    [array[r], array[i]] = [array[i], array[r]];
  }
  return array;
}

function random() {
  return rng();
}

function randomRange(min, max) {
  return rng() * (max - min) + min;
}

function randomRangeInt(min, max) {
  return Math.floor(rng() * (max - min + 1)) + min;
}

export { random, randomRange, randomRangeInt, seedRNG, shuffleArray };
